import { flash } from "@xola/ui-kit";
import { FormField, InputField, SwitchField } from "common";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { forwardRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { KioskFieldConfig, KioskImageFieldConfig, KioskPrinterPreferences, KioskPrinterPreferencesType } from "types";
import { xolaUri } from "@/api/xolaUri";
import { ImageUploadInput } from "@/components/Common/ImageUploadInput";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { useSeller } from "@/hooks/useAccount";

type KioskPrinterToggleType =
    | KioskPrinterPreferencesType.KIOSK_PRINTER_PRINT_RECEIPTS
    | KioskPrinterPreferencesType.KIOSK_PRINTER_PRINT_QR_TICKETS;

const printerToggleFields: Array<{ name: KioskPrinterToggleType; label: string }> = [
    { name: KioskPrinterPreferencesType.KIOSK_PRINTER_PRINT_RECEIPTS, label: "Print Purchase Receipts" },
    { name: KioskPrinterPreferencesType.KIOSK_PRINTER_PRINT_QR_TICKETS, label: "Print QR Code Tickets" },
];

interface KioskPrinterPreferencesFormProps {
    initialValues: KioskPrinterPreferences;
}

export const KioskPrinterPreferencesFields = forwardRef<
    FormikProps<KioskPrinterPreferences>,
    KioskPrinterPreferencesFormProps
>(({ initialValues }, ref) => {
    const seller = useSeller();
    const [printerImageConfigured, setPrinterImageConfigured] = useState(false);
    const [printerImageURL, setPrinterImageURL] = useState(
        initialValues[KioskPrinterPreferencesType.KIOSK_PRINTER_IMAGE],
    );
    const uploadURL = `${xolaUri}/api/sellers/${seller.id}/medias`;

    const updatePrinterImagePreference = (response: any) => {
        if (response.data?.id) {
            setPrinterImageURL(`${xolaUri}${response.data.src}`);
            setPrinterImageConfigured(true);
        } else {
            setPrinterImageConfigured(false);
        }
    };

    const deletePrinterImage = () => {
        setPrinterImageURL("");
        setPrinterImageConfigured(true);
    };

    type PrinterPreferenceFields = {
        KIOSK_PRINTER_IMAGE: KioskImageFieldConfig;
        KIOSK_PRINTER_MESSAGE: KioskFieldConfig;
    };

    const printerPreferenceFields: PrinterPreferenceFields = {
        KIOSK_PRINTER_IMAGE: {
            name: KioskPrinterPreferencesType.KIOSK_PRINTER_IMAGE,
            label: "Receipt / QR Code Ticket Header Image",
            src: printerImageURL,
            uploadUrl: uploadURL,
            onUpdated: updatePrinterImagePreference,
            hasDelete: true,
            onDelete: deletePrinterImage,
            tooltip: "Display a custom image at the top of printed receipt tickets.",
        },
        KIOSK_PRINTER_MESSAGE: {
            name: KioskPrinterPreferencesType.KIOSK_PRINTER_MESSAGE,
            label: "Receipt / QR Code Ticket Thank-you Message",
            children: null,
            tooltip: "A short text statement that appears at the end of a receipt ticket.",
            field: "text",
        },
    };

    const location = useLocation();

    useEffect(() => {
        if (!initialValues || Object.keys(initialValues).length === 0) return;

        const loadImg = async () => {
            setPrinterImageURL(initialValues[KioskPrinterPreferencesType.KIOSK_PRINTER_IMAGE]);
        };

        loadImg();
    }, [initialValues, location.pathname]);

    const handleSubmit = async (
        values: KioskPrinterPreferences,
        { setValues }: FormikHelpers<KioskPrinterPreferences>,
    ) => {
        const modifiedValues = {
            ...values,
            ...(printerImageConfigured && { [KioskPrinterPreferencesType.KIOSK_PRINTER_IMAGE]: printerImageURL }),
        };

        setValues(modifiedValues);
        return modifiedValues;
    };

    return (
        <Formik enableReinitialize innerRef={ref} initialValues={initialValues} onSubmit={handleSubmit}>
            {({ values, setFieldValue }) => (
                <ContentLayout.Section title="Print">
                    {/* Printer Image */}
                    <div
                        key={printerPreferenceFields.KIOSK_PRINTER_IMAGE.name}
                        data-testid={`prefs-kiosk-${printerPreferenceFields.KIOSK_PRINTER_IMAGE.label}-section`}
                    >
                        <FormField
                            name={printerPreferenceFields.KIOSK_PRINTER_IMAGE.name}
                            label={printerPreferenceFields.KIOSK_PRINTER_IMAGE.label}
                            tooltip={printerPreferenceFields.KIOSK_PRINTER_IMAGE.tooltip}
                        >
                            <ImageUploadInput
                                returnResponse
                                src={printerPreferenceFields.KIOSK_PRINTER_IMAGE.src}
                                uploadUrl={printerPreferenceFields.KIOSK_PRINTER_IMAGE.uploadUrl}
                                hasDelete={printerPreferenceFields.KIOSK_PRINTER_IMAGE.hasDelete}
                                onDelete={printerPreferenceFields.KIOSK_PRINTER_IMAGE.onDelete}
                                onUpdated={printerPreferenceFields.KIOSK_PRINTER_IMAGE.onUpdated}
                                onError={(error) => {
                                    return flash.show({ text: error, color: "danger" });
                                }}
                            />
                        </FormField>
                    </div>

                    {/* Receipt text string */}
                    <div data-testid={`prefs-kiosk-${printerPreferenceFields.KIOSK_PRINTER_MESSAGE.label}-section`}>
                        <FormField
                            name={printerPreferenceFields.KIOSK_PRINTER_MESSAGE.name}
                            label={printerPreferenceFields.KIOSK_PRINTER_MESSAGE.label}
                            tooltip={printerPreferenceFields.KIOSK_PRINTER_MESSAGE.tooltip}
                        >
                            <InputField
                                className="!items-start"
                                value={values[KioskPrinterPreferencesType.KIOSK_PRINTER_MESSAGE]}
                                onChange={(value) => {
                                    setFieldValue(printerPreferenceFields.KIOSK_PRINTER_MESSAGE.name, value);
                                }}
                            />
                        </FormField>
                    </div>

                    <div className="flex flex-col gap-[5px]">
                        {printerToggleFields.map(({ name, label }) => (
                            <div key={name} data-testid={`prefs-kiosk-${label}-section`}>
                                <SwitchField
                                    className="!items-start"
                                    isChecked={!!values[name]}
                                    onChange={(value) => setFieldValue(name, value)}
                                >
                                    {label}
                                </SwitchField>
                            </div>
                        ))}
                    </div>
                </ContentLayout.Section>
            )}
        </Formik>
    );
});
